/** Tiny promise wrapper around IndexedDB (no dependency). */

const DB_NAME = 'plume';
const DB_VERSION = 2;

export type StoreName = 'downloads' | 'progress' | 'kv';

/** Download records are per account: several accounts may download the same item. */
export const DOWNLOADS_KEY_PATH = ['serverUrl', 'userId', 'itemId'];

let dbPromise: Promise<IDBDatabase> | null = null;

/** v1 keyed downloads by `itemId` alone: re-create the store with the account key and copy the records. */
function migrateDownloadsStore(db: IDBDatabase, tx: IDBTransaction): void {
  const read = tx.objectStore('downloads').getAll();
  read.onsuccess = () => {
    db.deleteObjectStore('downloads');
    const store = db.createObjectStore('downloads', { keyPath: DOWNLOADS_KEY_PATH });
    for (const record of read.result as unknown[]) {
      try {
        store.put(record);
      } catch {
        // A record without an account cannot be keyed (never written by this app).
      }
    }
  };
}

/**
 * How long one database call may take. WebKit can lose its IndexedDB
 * connection while an iOS web app is in the background: requests then never
 * succeed or fail, and an await on them never returns (a progress sync stuck
 * that way held every later sync back for the rest of the visit).
 */
export const DB_TIMEOUT_MS = 5000;

export class DbTimeoutError extends Error {
  constructor(what: string) {
    super(`IndexedDB ${what} timed out`);
    this.name = 'DbTimeoutError';
  }
}

/** Forgets the connection, so the next call opens a new one (WebKit's lost connections never recover). */
function dropConnection(db?: IDBDatabase): void {
  const current = dbPromise;
  dbPromise = null;
  if (db) {
    try {
      db.close();
    } catch {
      /* already closed */
    }
  } else {
    void current?.then((d) => d.close()).catch(() => undefined);
  }
}

/** Settles with `work`, or rejects after `DB_TIMEOUT_MS` and drops the connection. */
function withDeadline<T>(what: string, work: Promise<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => {
      dropConnection();
      reject(new DbTimeoutError(what));
    }, DB_TIMEOUT_MS);
    work.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        clearTimeout(timer);
        reject(err);
      },
    );
  });
}

export function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  const opening = new Promise<IDBDatabase>((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (event) => {
      const db = req.result;
      if (!db.objectStoreNames.contains('downloads')) db.createObjectStore('downloads', { keyPath: DOWNLOADS_KEY_PATH });
      else if (event.oldVersion < 2 && req.transaction) migrateDownloadsStore(db, req.transaction);
      if (!db.objectStoreNames.contains('progress')) db.createObjectStore('progress', { keyPath: 'itemId' });
      if (!db.objectStoreNames.contains('kv')) db.createObjectStore('kv');
    };
    req.onsuccess = () => {
      const db = req.result;
      // Another tab upgraded the schema: close so it can proceed.
      db.onversionchange = () => {
        db.close();
        if (dbPromise === opening) dbPromise = null;
      };
      // The browser closed the connection (storage cleared, connection lost): reopen next time.
      db.onclose = () => {
        if (dbPromise === opening) dbPromise = null;
      };
      // An open that answers after its deadline: nobody waits for this connection.
      if (dbPromise !== opening) db.close();
      resolve(db);
    };
    req.onerror = () => {
      if (dbPromise === opening) dbPromise = null;
      reject(req.error);
    };
    req.onblocked = () => console.warn('[db] upgrade blocked by another tab');
  });
  dbPromise = opening;
  return opening;
}

function wrap<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/**
 * A transaction on a store. A connection that is closing (lost in the
 * background) throws on `transaction()`: it is dropped and a new one opened.
 */
async function openTransaction(name: StoreName, mode: IDBTransactionMode): Promise<IDBTransaction> {
  for (let attempt = 0; ; attempt++) {
    const db = await openDb();
    try {
      return db.transaction(name, mode);
    } catch (err) {
      if (attempt > 0 || (err as DOMException)?.name !== 'InvalidStateError') throw err;
      dropConnection(db);
    }
  }
}

/** Runs one request on a store, within `DB_TIMEOUT_MS`. */
function run<T>(what: string, name: StoreName, mode: IDBTransactionMode, call: (os: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return withDeadline(what, openTransaction(name, mode).then((tx) => wrap(call(tx.objectStore(name)))));
}

export async function dbGet<T>(name: StoreName, key: IDBValidKey): Promise<T | undefined> {
  return run(`get ${name}`, name, 'readonly', (os) => os.get(key)) as Promise<T | undefined>;
}

export async function dbGetAll<T>(name: StoreName): Promise<T[]> {
  return run(`getAll ${name}`, name, 'readonly', (os) => os.getAll()) as Promise<T[]>;
}

export async function dbPut<T>(name: StoreName, value: T, key?: IDBValidKey): Promise<void> {
  await run(`put ${name}`, name, 'readwrite', (os) => os.put(value, key));
}

export async function dbDelete(name: StoreName, key: IDBValidKey): Promise<void> {
  await run(`delete ${name}`, name, 'readwrite', (os) => os.delete(key));
}

/** Read-modify-write inside one transaction so concurrent updates don't clobber each other. */
export async function dbUpdate<T>(name: StoreName, key: IDBValidKey, fn: (prev: T | undefined) => T | undefined): Promise<T | undefined> {
  return withDeadline(
    `update ${name}`,
    openTransaction(name, 'readwrite').then(
      (tx) =>
        new Promise<T | undefined>((resolve, reject) => {
          const os = tx.objectStore(name);
          let result: T | undefined;
          const getReq = os.get(key);
          getReq.onsuccess = () => {
            result = fn(getReq.result as T | undefined);
            if (result === undefined) os.delete(key);
            else if (os.keyPath) os.put(result);
            else os.put(result, key);
          };
          tx.oncomplete = () => resolve(result);
          tx.onerror = () => reject(tx.error);
          tx.onabort = () => reject(tx.error);
        }),
    ),
  );
}
